import React, { useEffect, useRef, useState } from 'react';

// Top navigation (Phase 1).
//
// 7 items: 4 simple links + 3 dropdowns (Relationships, Documents,
// Utilities). Pill buttons; active item filled red; dropdowns open on
// hover AND click AND Enter/Space, close on outside-click and Escape.
// Dropdown children support {divider:true} separators and {soon:true}
// disabled (greyed, non-clickable) items.
const NAV_ITEMS = [
  { kind: 'link',     id: 'dashboard',     label: 'Dashboard' },
  { kind: 'dropdown', id: 'relationships', label: 'Relationships', children: [
    { id: 'clients',     label: 'Clients' },
    { id: 'consultants', label: 'Consultants' },
    { id: 'ntrg',        label: 'NTRG' },
    { id: 'onboarding',  label: 'Onboarding' },
  ]},
  { kind: 'link',     id: 'properties',    label: 'Properties' },
  { kind: 'link',     id: 'calendar',      label: 'Calendar' },
  { kind: 'dropdown', id: 'docs',          label: 'Documents', children: [
    { id: 'documents',   label: 'Documents' },
    { id: 'intake',      label: 'Document Intake' },
  ]},
  { kind: 'link',     id: 'reporting',    label: 'Reports', also: ['report'] },   // Live Reporting (step 8 part 6)
  { kind: 'dropdown', id: 'utilities',     label: 'Utilities', children: [
    { id: 'activity',   label: 'Activity' },
    { id: 'assessors_collectors', label: 'Assessors & Collectors' },
    { id: 'deliveries', label: 'Client Deliveries' },
    { id: 'exchange',   label: 'File Exchange' },
    { id: 'open_questions', label: 'Open Questions' },
    { id: 'tablemgmt',  label: 'Table Management' },
    { id: 'tax_savings_packet', label: 'Tax Savings Packet' },
    { id: 'templates',  label: 'Templates', soon: true },
    { id: 'users',      label: 'User Management' },
    { id: 'admin',      label: 'Admin Console' },
    { divider: true },
    { id: 'parcels',    label: 'Parcels' },
    { id: 'scanner',    label: 'Document Scanner' },
    { id: 'extractor',  label: 'Notice Extractor' },
    { id: 'audit',      label: 'Notice Audit' },
    { id: 'tasks',      label: 'Task Engine' },
  ]},
];

const PILL_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  height: '38px',
  padding: '8px 15px',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  border: 'none',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--slate)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function isActive(item, activePage) {
  if (item.kind === 'link') return item.id === activePage || !!item.also?.includes(activePage);
  return item.children?.some(c => c.id === activePage);
}

export default function Nav({ activePage, onActivePageChange }) {
  const [openId, setOpenId] = useState(null);
  const navRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (!navRef.current?.contains(e.target)) setOpenId(null);
    }
    function onKey(e) { if (e.key === 'Escape') setOpenId(null); }
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  function go(pageId) {
    setOpenId(null);
    onActivePageChange(pageId);
  }

  function pillStyle(active) {
    if (active) return { ...PILL_BASE, background: 'var(--red)', color: 'white' };
    return PILL_BASE;
  }

  return (
    <nav ref={navRef} aria-label="Primary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {NAV_ITEMS.map(item => {
        const active = isActive(item, activePage);
        if (item.kind === 'link') {
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => go(item.id)}
              style={pillStyle(active)}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--line-soft)'; e.currentTarget.style.color = 'var(--charcoal)'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--slate)'; } }}>
              {item.label}
            </button>
          );
        }
        const isOpen = openId === item.id;
        return (
          <div
            key={item.id}
            style={{ position: 'relative' }}
            onMouseEnter={() => {
              if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
              setOpenId(item.id);
            }}
            onMouseLeave={() => {
              closeTimerRef.current = setTimeout(() => {
                setOpenId(prev => (prev === item.id ? null : prev));
                closeTimerRef.current = null;
              }, 120);
            }}>
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={isOpen}
              onClick={() => setOpenId(prev => (prev === item.id ? null : item.id))}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpenId(prev => (prev === item.id ? null : item.id));
                }
              }}
              style={pillStyle(active)}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--line-soft)'; e.currentTarget.style.color = 'var(--charcoal)'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--slate)'; } }}>
              {item.label} <span style={{ fontSize: '10px', marginLeft: '2px' }}>▾</span>
            </button>
            {isOpen && (
              <div role="menu" style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                background: 'var(--panel)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius)',
                boxShadow: 'var(--shadow)',
                minWidth: '200px',
                padding: '6px',
                zIndex: 100,
              }}>
                {item.children.map((child, idx) => {
                  if (child.divider) {
                    return (
                      <div
                        key={`div-${idx}`}
                        role="separator"
                        style={{ height: '1px', background: 'var(--line)', margin: '6px 4px' }}
                      />
                    );
                  }
                  if (child.soon) {
                    return (
                      <div
                        key={child.id}
                        aria-disabled="true"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          padding: '9px 12px',
                          color: 'var(--slate)',
                          fontSize: '13px',
                          fontWeight: 500,
                          opacity: 0.55,
                          cursor: 'default',
                          fontFamily: 'inherit',
                        }}>
                        {child.label}
                      </div>
                    );
                  }
                  const childActive = child.id === activePage;
                  return (
                    <button
                      key={child.id}
                      type="button"
                      role="menuitem"
                      onClick={() => go(child.id)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '9px 12px',
                        background: childActive ? 'var(--red-tint)' : 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: childActive ? 'var(--red-dark)' : 'var(--ink)',
                        fontSize: '13px',
                        fontWeight: childActive ? 700 : 500,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--red-tint)'; e.currentTarget.style.color = 'var(--red-dark)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = childActive ? 'var(--red-tint)' : 'transparent'; e.currentTarget.style.color = childActive ? 'var(--red-dark)' : 'var(--ink)'; }}>
                      {child.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
